import axios from 'axios';
import type { DateTime } from 'luxon';
import type { SystemResponseRatingEnum } from '../enums/ai';

export default class AiChatService
{
    public static async chat(input: string): Promise<UserChatResponse[]>
    {
        const response = await axios.get<UserChatResponse[]>('admin/ai-chat/chat', {
            params: {
                input: input
            }
        });

        return response.data;
    }

    public static async getSettings(): Promise<ChatSettingsModel>
    {
        return (await axios.get<ChatSettingsModel>(`admin/ai-chat/get-settings`)).data;
    }

    public static async saveSettings(model: ChatSettingsModel): Promise<any>
    {
        return (await axios.post('admin/ai-chat/save-settings', model)).data;
    }

    public static async saveEmbeddings(): Promise<any>
    {
        return (await axios.post('admin/ai-chat/save-embeddings', null)).data;
    }

    public static async getThread(): Promise<ChatInteraction[]>
    {
        return (await axios.get<ChatInteraction[]>(`admin/ai-chat/thread`)).data;
    }

    public static async rateInteraction(interactionLogId: number, rating: SystemResponseRatingEnum): Promise<any>
    {
        return (await axios.post('admin/ai-chat/rate-interaction', null, {
            params: {
                interactionLogId: interactionLogId,
                rating: rating
            }
        })).data;
    }

    public static async getLogsList(filter: AiChatFilterModel, pager: Pager): Promise<Pagination<Resource<LogsListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<LogsListItemModel>>>('admin/ai-chat/responses-logs', {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async fetchLog(logId: number): Promise<LogsListItemModel>
    {
        return (await axios.get<LogsListItemModel>(`admin/ai-chat/responses-logs/${logId}`)).data;
    }

    public static async fetchThreadLog(threadLogId: number): Promise<ChatThreadLogDetails>
    {
        return (await axios.get<ChatThreadLogDetails>(`admin/ai-chat/thread-logs/${threadLogId}`)).data;
    }
}

export interface LogsListItemModel
{
    id: number;
    threadLogId: number;
    dateCreatedUtc: DateTime;
    startingPrompt: string;
    userInput: string;
    systemResponse: string;
    rating: SystemResponseRatingEnum;
    imagesDictionary: Record<string, string>;
    preparedQuestionSet: string;
    matchedEmbeddings: InteractionLogMatchedEmbedding[];
}

export interface AiChatFilterModel
{
    rating?: SystemResponseRatingEnum;
    dateCreatedFrom?: DateTime;
    dateCreatedDue?: DateTime;
    isRated?: boolean;
}

export interface UserChatResponse
{
    contentPart: string;
    isFinal: boolean;
    interactionLogId: number;
    error?: string;
    outlineImages?: Record<string, string>;
}

export interface ChatSettingsModel
{
    enableAiChat: boolean;
    azureResourceUri: string;
    chatDeploymentName: string;
    embeddingDeploymentName: string;
    azureOpenAIKey: string;
    outlineApiKey: string;
    outlineMainDocumentId: string;
    langfusePublicKey: string;
    langfuseSecretKey: string;
}

export interface ChatInteraction
{
    interactionDateUtc: DateTime;
    userInput: string;
    systemResponse: string;
    interactionLogId?: number;
    isRated?: boolean;
    imagesDictionary: Record<string, string>;
}

export interface ChatThreadLogDetails
{
    dateCreatedUtc: DateTime;
    userEmail: string;
    userName: string;
    interactions: ChatThreadInteractionLog[];
}

export interface ChatThreadInteractionLog
{
    dateCreatedUtc: DateTime;
    startingPrompt: string;
    userInput: string;
    systemResponse: string;
    rate: SystemResponseRatingEnum;
    imagesDictionary: Record<string, string>;
}

export interface InteractionLogMatchedEmbedding
{
    id: number;
    name: string;
    score?: number;
}
